import React, { useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import Swal from "sweetalert2";
import { setupManagementUrl } from "../../../config";
import DynamicForm from "../../../data/Axios/DynamicForm";
import { generateAndExecuteMutation, graphqlQuery } from "../../../data/Axios/DynamicService";

const base_url = setupManagementUrl.uri;

const ProductsForm = (props) => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const companyId = parseInt(decodedToken.CompanyId, 10);

  const [productCategoryOptions, setProductCategoryOptions] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  const [formData, setFormData] = useState({
    id: props.data ? props.data.id : "",
    productName: props.data ? props.data.productName : "",
    productCategory: "", 
    unitPrice: props.data ? props.data.unitPrice : 0,
    costPrice: props.data ? props.data.costPrice : 0,
    retailPrice: props.data ? props.data.retailPrice : 0,
    wholesalePrice: props.data ? props.data.wholesalePrice : 0,
    companyId: props.data ? props.data.companyId : companyId,
    isService: !!props.data?.isService
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const categoryQuery = `
          query AllProductCategories {
            productCategories {
              id
              name
            }
          }
        `;
        const categoryResponse = await graphqlQuery(categoryQuery, base_url);
        setProductCategoryOptions(categoryResponse || []);
            // Prefill the productCategory if we're editing and have data
          if (props.data && props.data.productCategory) {
            const categoryName = props.data.productCategory;
            
            // Find the category ID by matching the name
            const foundCategory = categoryResponse.find(
              category => category.name === categoryName
            );
            
            if (foundCategory) {
              setFormData(prev => ({
                ...prev,
                productCategory: foundCategory.id
              }));
            }
          }
        
      } catch (error) {
        console.error("Error fetching product categories:", error);
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: "Failed to fetch Product Category options.",
        });
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, []);

  const handleFieldChange = async (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field.name]: value,
    }));
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const {
        productName,
        productCategory,
        unitPrice,
        costPrice,
        retailPrice,
        wholesalePrice,
        isService,    
      } = values;

      const parsedUnitPrice = parseFloat(unitPrice);
      const parsedCostPrice = parseFloat(costPrice);
      const parsedRetailPrice = parseFloat(retailPrice);
      const parsedWholesalePrice = parseFloat(wholesalePrice);

      if (
        parsedRetailPrice < parsedCostPrice ||
        parsedWholesalePrice < parsedCostPrice
      ) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: "Retail and Wholesale prices cannot be less than the Buying Price.",
        });
        setSubmitting(false);
        return;
      }

      const mutationData = {
        productName,
        productCategory: productCategory,
        unitPrice: parsedUnitPrice,
        costPrice: parsedCostPrice,
        retailPrice: parsedRetailPrice,
        wholesalePrice: parsedWholesalePrice,
        isInventory: true,
        isActive: true,
        companyId,
        isService: isService ? 1 : 0,
      };

      if (props.isEditing) {
        mutationData.id = props.data.id;
      }

      const response = await generateAndExecuteMutation(
        props.isEditing ? "updateProducts" : "createProducts",
        mutationData,
        "products",
        base_url
      );

      if (Object.values(response)[0] != null) {
        props.onClose();
        props.onAction();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Product ${props.isEditing ? "updated" : "created"} successfully.`,
        });
      }
    } catch (error) {
      console.error("Error submitting product:", error);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }
    setSubmitting(false);
  };

  const fields = [
    {
      name: "productName",
      label: "Product Name *",
      type: "text",
      gridColumn: "span 2",
      required: true,
    },
    {
      name: "productCategory",
      label: "Product Category *",
      type: "select",
      gridColumn: "span 2",
      required: true,
      options: productCategoryOptions.map((category) => ({
        value: category.id,
        label: category.name,
      })),
    },
    {
      name: "costPrice",
      label: "Buying Price *",
      type: "number",
      gridColumn: "span 2",
      required: true,
    },
    {
      name: "unitPrice",
      label: "Selling Price *",
      type: "number",
      gridColumn: "span 2",
      required: true,
    },
    {
      name: "retailPrice",
      label: "Retail Price *",
      type: "number",
      gridColumn: "span 2",
      required: true,
    },
    {
      name: "wholesalePrice",
      label: "Wholesale Price *",
      type: "number",
      gridColumn: "span 2",
      required: true,
    },
    {
      name: "isService",
      label: "Is It A Service Product ? *",
      type: "checkbox",
      gridColumn: "span 2",
      required: true,
    }
  ];

  if (loadingCategories) {
    return <CircularProgress />;
  }

  return (
    <DynamicForm
      initialValues={formData}
      fields={fields}
      onFieldChange={handleFieldChange}
      onSubmit={handleSubmit}
      title={props.isEditing ? "Edit Product" : "Create Product"}
      subtitle={
        props.isEditing ? "Edit an existing product" : "Create new product"
      }
    />
  );
};

export default ProductsForm;
